import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { GeoUnitSelect } from '@/components/common/GeoUnitSelect';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import { useConfirmBeforeClose } from '@/hooks/useConfirmBeforeClose';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { activeExamBoardsQueryOptions } from '@/features/examBoards/api/examBoards.api';
import { activeInstituteTypesQueryOptions } from '@/features/instituteTypes/api/instituteTypes.api';
import { instituteFormSchema, type InstituteFormValues } from '../types/institute.types';

interface SectionProps {
  cols?: 2 | 3;
  children: React.ReactNode;
}

function Section({ cols = 2, children }: SectionProps) {
  const gridClass =
    cols === 3 ? 'grid grid-cols-1 gap-4 sm:grid-cols-3' : 'grid grid-cols-1 gap-4 sm:grid-cols-2';
  return (
    <section className="rounded-xl border bg-card p-5 shadow-sm">
      <div className={gridClass}>{children}</div>
    </section>
  );
}

interface InstituteFormProps {
  defaultValues: InstituteFormValues;
  initialDivisionId?: number | undefined;
  initialDistrictId?: number | undefined;
  onSubmit: (values: InstituteFormValues) => void;
  onCancel?: () => void;
  isPending: boolean;
  submitLabel: string;
  pendingLabel: string;
}

export function InstituteForm({
  defaultValues,
  initialDivisionId,
  initialDistrictId,
  onSubmit,
  onCancel,
  isPending,
  submitLabel,
  pendingLabel,
}: InstituteFormProps) {
  const form = useForm<InstituteFormValues>({
    resolver: zodResolver(instituteFormSchema),
    defaultValues,
  });

  const { askBeforeClose, discardChangesDialog } = useConfirmBeforeClose(form.formState.isDirty);

  function handleCancel() {
    if (!onCancel) return;
    askBeforeClose(onCancel);
  }

  const { data: instituteTypes, isLoading: typesLoading } = useQuery(
    activeInstituteTypesQueryOptions(),
  );
  const { data: examBoards, isLoading: examBoardsLoading } = useQuery(
    activeExamBoardsQueryOptions(),
  );

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <Section>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-0.5">
                    Name<span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Dhaka Model School" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nameBn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name (Bangla)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="ঢাকা মডেল স্কুল" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      placeholder="A short description of this institute"
                      rows={3}
                      className="min-h-20 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:border-ring focus:ring-1 focus:ring-ring/30 resize-y"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <Section>
            <FormField
              control={form.control}
              name="instituteTypeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-0.5">
                    Institute Type<span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <OptionSelect
                      options={instituteTypes}
                      isLoading={typesLoading}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select institute type"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="examBoardId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-0.5">
                    Exam Board<span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <OptionSelect
                      options={examBoards}
                      isLoading={examBoardsLoading}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select exam board"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <Section>
            <FormField
              control={form.control}
              name="geoUnitId"
              render={({ field, fieldState }) => (
                <FormItem className="sm:col-span-2">
                  <FormControl>
                    <GeoUnitSelect
                      value={field.value}
                      onChange={field.onChange}
                      divisionId={initialDivisionId}
                      districtId={initialDistrictId}
                      required
                      error={fieldState.error?.message}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </Section>

          <Section cols={3}>
            <FormField
              control={form.control}
              name="contactName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-0.5">
                    Contact Name<span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Karim Uddin" autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contactNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-0.5">
                    Contact Number<span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="01711223344"
                      autoComplete="off"
                      inputMode="numeric"
                      maxLength={11}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="eiinNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>EIIN Number</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="123456" autoComplete="off" inputMode="numeric" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <Section cols={3}>
            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-0.5">
                    Start Date<span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <DatePickerInput
                      value={field.value}
                      onChange={field.onChange}
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="gap-0.5">
                    End Date<span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <DatePickerInput
                      value={field.value}
                      onChange={field.onChange}
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={field.value ? 'default' : 'outline'}
                        onClick={() => field.onChange(true)}
                        className="h-10 sm:h-9"
                      >
                        Active
                      </Button>
                      <Button
                        type="button"
                        variant={!field.value ? 'default' : 'outline'}
                        onClick={() => field.onChange(false)}
                        className="h-10 sm:h-9"
                      >
                        Inactive
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Section>

          <div className="flex items-center justify-end gap-3">
            {onCancel ? (
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
                Cancel
              </Button>
            ) : null}
            <Button type="submit" disabled={isPending} className="gap-1.5">
              {isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {pendingLabel}
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </div>
        </form>
      </Form>

      {discardChangesDialog}
    </>
  );
}
